import React, { useContext, useMemo, useState } from "react";
import ProfileCard from "../../../common/ProfileCard/ProfileCard";
import DietPlanTab from "../../../features/diet/workflow/plan/view/DietPlanTab";
import ActivityTab from "../../../features/workouts/workflow/profile/view/ActivityTab";
import Notes from "../../../features/notes/workflow/profile/view/NotesTab";
import UserAnalytics from "../../Dashboard/UserInfoDashboard/userAnalytics/UserAnalytics";
import Subscriptions from "../../Dashboard/UserInfoDashboard/subscriptions/Subscriptions";
import { ApiContext } from "../../../context/MyContext";
import { hasButtonPermission } from "../../../utils/accessControl";
import { serializeUserNotesKey } from "../../../utils/listCache/caches";

/** No-op used where a child expects an alert callback but the rail has none. */
const noop = () => {};

/**
 * Tab definitions per mode. The 'compact' set powers the chat-focus rail (3
 * tabs); the 'full' set powers the client-focus layout (6 tabs). Each tab name
 * must have a matching case in renderTabBody below. "Subscriptions" is dropped
 * at render time for viewers without the view-subscriptions-tab permission.
 * @type {Record<string, string[]>}
 */
const TABS_BY_MODE = {
  compact: ["Nutrition", "Workout", "Notes"],
  full: [
    "Nutrition",
    "Workout",
    "Notes",
    "Subscriptions",
    "Activity",
    "Analytics",
  ],
  // Mirrors UserInfoDashboard (/dashboard/:id) exactly — same labels, same
  // order, and the same full-size tab bodies — so the Users split view is the
  // screen coaches already know rather than a second, subtly different one.
  dashboard: [
    "Nutrition",
    "Activity",
    "User Analytics",
    "Notes",
    "Subscriptions",
  ],
};

/** Modes that render full-size tab bodies rather than the compact variants. */
const FULL_SIZE_MODES = new Set(["dashboard"]);

/** Stable identity for the default, so it never re-triggers a memo. */
const NO_LEADING_TABS = Object.freeze([]);

/**
 * A caller-supplied tab pinned before the mode's own tabs.
 *
 * The rail stays deliberately ignorant of what these contain — the Users split
 * view uses one for chat, and `ClientProfilePanel` passes none, which is how a
 * support agent's drawer keeps coaching-room content out by construction
 * rather than by a runtime check.
 *
 * @typedef {Object} LeadingTab
 * @property {string} id - Tab id, and the value passed to `onTabChange`.
 * @property {string} label - Visible label.
 * @property {number} [badge=0] - Count pill beside the label; hidden at 0.
 * @property {boolean} [fullBleed=false] - Render the body with no padding and
 *   no scroll container, because the tab manages its own layout. A chat thread
 *   scrolls itself and must not sit inside a second scroller.
 * @property {() => React.ReactNode} render - Called only while this tab is
 *   active, so an expensive body is never mounted behind another tab.
 */

/**
 * Right-hand client rail for the Messages workspace. Pins the same ProfileCard
 * used on the user profile page at the top and mounts the existing dashboard
 * sections beneath a tab bar — one source of truth for the profile summary
 * and for Nutrition/Workout/Notes/Subscriptions/Activity/Analytics.
 *
 * @param {Object} props
 * @param {string} props.clientId - FlexBoard user id of the room's client.
 * @param {string} [props.roomId] - Owning room id (reserved for future use).
 * @param {'compact'|'full'|'dashboard'} [props.mode='compact'] - Which tab set
 *   to render. 'dashboard' leads with Chat, for the Users split view.
 * @param {'pane'|'flow'} [props.layout='pane'] - 'pane' pins the profile card
 *   and tab bar with only the tab body scrolling, for the fixed-height desktop
 *   inline pane. 'flow' drops all internal scroll containers so everything
 *   scrolls together as one screen — used when this renders inside a mobile
 *   `Drawer`, which already provides the single scroll container.
 * @param {string|null} [props.cacheKey=null] - Forwarded to `ProfileCard` to
 *   opt this client's profile payload into `myUserProfileCache`. Left null by
 *   the comms rails, which always want a fresh profile.
 * @param {boolean} [props.showProfileCard=true] - Set false when the parent
 *   already renders its own profile header, to avoid a second ProfileCard
 *   (and a second profile fetch) on the same screen.
 * @param {string} [props.returnTo] - Where the diet/workout plan editors send
 *   the coach when they finish. Defaults inside each tab to `/dashboard/:id`;
 *   the Users split view passes its own URL so the round trip lands back on
 *   the open panel instead of the full page.
 * @param {LeadingTab[]} [props.leadingTabs=[]] - Extra tabs pinned before the
 *   mode's own. The first one becomes the default active tab.
 * @param {string} [props.activeTab] - Passing this makes the rail controlled;
 *   omit it and the rail keeps its own tab state as before.
 * @param {(tabId: string) => void} [props.onTabChange] - Fired on every tab
 *   click, controlled or not.
 */
// `roomId` is accepted for call-site symmetry with the other rails but isn't
// read yet; the underscore keeps the lint rule satisfied without changing the
// public prop name.
export function ClientRail({
  clientId,
  roomId: _roomId,
  mode = "compact",
  layout = "pane",
  cacheKey = null,
  showProfileCard = true,
  returnTo,
  leadingTabs = NO_LEADING_TABS,
  activeTab: activeTabProp,
  onTabChange,
}) {
  const isFlow = layout === "flow";
  const { employeeData, departmentsWithRoles } = useContext(ApiContext);
  const canViewSubscriptionsTab = hasButtonPermission(
    "view-subscriptions-tab",
    "user-dashboard",
    employeeData,
    departmentsWithRoles
  );

  const baseTabs = useMemo(() => {
    const base = TABS_BY_MODE[mode] || TABS_BY_MODE.compact;
    return canViewSubscriptionsTab
      ? base
      : base.filter((tab) => tab !== "Subscriptions");
  }, [mode, canViewSubscriptionsTab]);

  // The mode's own tabs are plain strings; leading tabs are descriptors. One
  // list of descriptors so the strip renders them identically.
  const tabItems = [
    ...leadingTabs,
    ...baseTabs.map((id) => ({ id, label: id })),
  ];

  // Held as null rather than seeded with the first tab: the permission check
  // above can change the tab set, and a seeded value would pin a tab that is
  // no longer there. Resolving lazily also means "first tab wins by default"
  // needs no extra rule.
  const [uncontrolledTab, setUncontrolledTab] = useState(null);
  const isControlled = activeTabProp !== undefined;
  const desiredTab = isControlled ? activeTabProp : uncontrolledTab;
  const currentTab = tabItems.some((t) => t.id === desiredTab)
    ? desiredTab
    : tabItems[0]?.id;

  const selectTab = (id) => {
    if (!isControlled) setUncontrolledTab(id);
    onTabChange?.(id);
  };

  const leadingTab = leadingTabs.find((t) => t.id === currentTab) || null;

  // In full-size modes the bodies get no `variant`, which is what
  // UserInfoDashboard passes — the tabs then render at their normal size.
  const isFullSize = FULL_SIZE_MODES.has(mode);
  const paneVariant = isFullSize ? undefined : "compact";

  const renderTabBody = () => {
    switch (currentTab) {
      case "Nutrition":
        return (
          <div className="w-full max-w-full overflow-x-auto">
            <DietPlanTab
              profileId={clientId}
              showAlert={noop}
              returnTo={returnTo}
            />
          </div>
        );
      case "Workout":
        return (
          <ActivityTab
            profileId={clientId}
            variant="compact"
            returnTo={returnTo}
          />
        );
      case "Notes":
        // Cache key derived here rather than taken as a prop: it is a pure
        // function of the client, so every rail that shows this tab gets the
        // instant paint without each call site having to remember to opt in.
        return (
          <Notes
            profileId={clientId}
            variant={paneVariant}
            cacheKey={serializeUserNotesKey(clientId)}
          />
        );
      case "Subscriptions":
        return (
          <div className="w-full max-w-full overflow-x-auto">
            <Subscriptions profileId={clientId} showAlert={noop} />
          </div>
        );
      case "Activity":
        return (
          <div className="w-full max-w-full overflow-x-auto">
            <ActivityTab
              profileId={clientId}
              variant={paneVariant}
              returnTo={returnTo}
            />
          </div>
        );
      // "Analytics" is the compact rails' label; "User Analytics" is the
      // dashboard's. Same body.
      case "Analytics":
      case "User Analytics":
        return (
          <div className="w-full max-w-full overflow-x-auto">
            <UserAnalytics profileId={clientId} variant={paneVariant} />
          </div>
        );
      default:
        return null;
    }
  };

  const bodyClass = (() => {
    if (leadingTab?.fullBleed) {
      return isFlow ? "" : "flex min-h-0 flex-1 flex-col overflow-hidden";
    }
    return isFlow ? "p-3" : "min-h-0 flex-1 overflow-y-auto p-3";
  })();

  return (
    <aside
      className={`w-full max-w-full bg-app-bg ${
        isFlow ? "" : "flex h-full flex-col"
      }`}
    >
      {/* Profile card — pinned in 'pane' mode, flows with the rest in 'flow' mode */}
      {showProfileCard && (
        <div
          className={`border-b border-gray-200 bg-white p-3 overflow-x-auto ${
            isFlow ? "" : "flex-shrink-0"
          }`}
        >
          <ProfileCard
            profileId={clientId}
            showCallControls={false}
            showPhotoControls={false}
            cacheKey={cacheKey}
          />
        </div>
      )}

      {/* Tab bar — sticky in 'flow' mode so it stays reachable while scrolling */}
      <div
        role="tablist"
        aria-label="Client details"
        className={`flex gap-1 overflow-x-auto border-b border-gray-200 bg-white px-2 ${
          isFlow ? "sticky top-0 z-10" : "flex-shrink-0"
        }`}
      >
        {tabItems.map(({ id, label, badge = 0 }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={currentTab === id}
            className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap py-2 px-3 text-sm text-gray-600 focus:outline-none ${
              currentTab === id ? "bg-input-bg text-color-text" : ""
            }`}
            onClick={() => selectTab(id)}
          >
            {label}
            {badge > 0 && (
              <span className="rounded-lg bg-[#FAEEDA] px-1.5 text-[10px] font-bold leading-4 text-[#633806]">
                {badge > 99 ? "99+" : badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab body — its own scroll container in 'pane' mode; flows with the
          page in 'flow' mode (the Drawer's own scroll container handles it).
          A full-bleed leading tab opts out of both: it lays itself out. */}
      <div className={bodyClass}>
        {leadingTab ? leadingTab.render() : renderTabBody()}
      </div>
    </aside>
  );
}

export default ClientRail;
